import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { FocusTrap } from 'focus-trap-react';
import { StyledDivider, StyledLoading } from '@whitelabel/rentalcover-shared/styled';
import {
  profileLink,
  protectionLink,
  claimDetailsLink,
  claimsLink,
  logoutLink,
  offersLink,
} from '@whitelabel/rentalcover-shared/helpers/navLinks';
import {
  StyledCollapse,
  StyledNavCustomer,
  StyledScrollableContainer,
} from '@whitelabel/rentalcover-shared/styled/StyledCollapsibleNav';
import CustomerDetails from '@whitelabel/rentalcover-shared/components/CustomerDetails';
import rentalcoverSharedMessages from '@whitelabel/rentalcover-shared/helpers/messages/commonMsg';
import { HelpButton } from '@whitelabel/rentalcover-shared/components/NavButtons';
import NavItem from '@whitelabel/rentalcover-shared/components/NavItem';
import { LocaleButtonMobile } from '@whitelabel/rentalcover-shared/components/LocaleButton/LocaleButtonMobile';

import { getButtonProps } from '@whitelabel/rentalcover-shared/helpers/button';
import { useAppSelector } from '../../../../../store';
import { selectCurrentCustomer } from '../../../../../store/slices/customer';
import { useTypedRouter, useRCOffers } from '../../../../../helpers/hooks';
import LocaleModal from '../../../../../components/LocaleModal';
import { StyledMainButton } from './styledCollapsibleNav';

interface ICollapsibleNavProps {
  isMobileNavOpen: boolean;
  claimReference?: string;
}

const CollapsibleNav = ({ isMobileNavOpen, claimReference }: ICollapsibleNavProps) => {
  const intl = useIntl();
  const { locale } = useTypedRouter();
  const customer = useAppSelector(selectCurrentCustomer);
  const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false);
  const { showOffers } = useRCOffers();

  const toggleLocaleModal = () => {
    setIsLocaleModalOpen(!isLocaleModalOpen);
  };

  const loggedInSection = customer && (
    <StyledNavCustomer className="nav-customer">
      {!customer ? (
        <StyledLoading />
      ) : (
        <>
          <CustomerDetails customer={customer} />
          {claimReference ? (
            <NavItem
              {...claimDetailsLink(intl, claimReference)}
              {...getButtonProps({ link: claimDetailsLink(intl, claimReference).path, locale })}
            />
          ) : (
            <>
              <NavItem {...protectionLink(intl)} {...getButtonProps({ link: protectionLink(intl).path, locale })} />
              <NavItem {...profileLink(intl)} {...getButtonProps({ link: profileLink(intl).path, locale })} />
              <NavItem {...claimsLink(intl)} {...getButtonProps({ link: claimsLink(intl).path, locale })} />
              {showOffers && (
                <NavItem {...offersLink(intl)} {...getButtonProps({ link: offersLink(intl).path, locale })} />
              )}
            </>
          )}
        </>
      )}
      <StyledDivider />
    </StyledNavCustomer>
  );

  return isMobileNavOpen ? (
    <FocusTrap
      active
      focusTrapOptions={{
        escapeDeactivates: true,
        clickOutsideDeactivates: true,
      }}
    >
      <StyledCollapse $isOpen={isMobileNavOpen} aria-hidden={!isMobileNavOpen}>
        <StyledScrollableContainer>
          {loggedInSection}
          <div className="nav-common">
            <p className="nav-settings-submenu">{intl.formatMessage(rentalcoverSharedMessages.settingsSubmenuText)}</p>
            <LocaleButtonMobile toggle={toggleLocaleModal} />
            <StyledDivider $gap="1rem" />
            <HelpButton />
            {customer && (
              <>
                <StyledDivider $gap="1rem" />
                <NavItem {...logoutLink(intl)} {...getButtonProps({ link: logoutLink(intl).path, locale })} />
              </>
            )}
          </div>
        </StyledScrollableContainer>
        <StyledMainButton tag="a" href={`/${locale}/`} className="get-quote" buttonType="primary">
          {intl.formatMessage(rentalcoverSharedMessages.getQuoteButton)}
        </StyledMainButton>
        <LocaleModal isOpen={isLocaleModalOpen} toggle={toggleLocaleModal} />
      </StyledCollapse>
    </FocusTrap>
  ) : null;
};

export default CollapsibleNav;
